import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertCircle, Check, FileCheck2, LoaderCircle, LockKeyhole } from "lucide-react";
import ContractSummary from "@/components/contracts/ContractSummary";
import { canSubmitContract, contractApi, formatEuro, publicContractError } from "@/lib/contractApi";

export default function PublicContract() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [party, setParty] = useState({ name: "", email: "" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acknowledgedWithdrawal, setAcknowledgedWithdrawal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    contractApi
      .getInvitation(token)
      .then(({ data }) => {
        if (!active) return;
        setContract(data);
        setParty({
          name: data.responsible_party?.name || "",
          email: data.responsible_party?.email || "",
        });
      })
      .catch((requestError) => active && setError(publicContractError(requestError)))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmitContract(party, acceptedTerms, acknowledgedWithdrawal)) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await contractApi.acceptInvitation(token, {
        responsible_party: { name: party.name.trim(), email: party.email.trim() },
        accepted_terms: acceptedTerms,
        acknowledged_withdrawal_privacy: acknowledgedWithdrawal,
      });
      sessionStorage.setItem(`contract-result:${token}`, JSON.stringify(data));
      navigate(`/vertrag/${token}/erfolg`, { state: data, replace: true });
    } catch (requestError) {
      setError(publicContractError(requestError));
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PublicShell>
        <div className="flex min-h-[50vh] items-center justify-center gap-3 text-slate-600">
          <LoaderCircle className="h-5 w-5 animate-spin" /> Vertrag wird geladen …
        </div>
      </PublicShell>
    );
  }

  if (error && !contract) {
    return (
      <PublicShell>
        <div className="mx-auto max-w-xl rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <AlertCircle className="mx-auto mb-4 h-10 w-10 text-red-700" />
          <h1 className="text-2xl font-semibold">Vertrag nicht verfügbar</h1>
          <p className="mt-3 text-slate-600">{error}</p>
          <p className="mt-6 text-sm text-slate-500">Kontakt: kantine@creativey.media</p>
        </div>
      </PublicShell>
    );
  }

  const legal = contract.legal_snapshots || {};
  const submitReady = canSubmitContract(party, acceptedTerms, acknowledgedWithdrawal);
  return (
    <PublicShell>
      <main className="mx-auto max-w-5xl">
        <header className="mb-8 max-w-3xl">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-red-800">
            <LockKeyhole className="h-3.5 w-3.5" /> Persönlicher Vertragslink
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-5xl">Nutzungsvertrag Feuerwehr Kantine</h1>
          <p className="mt-4 text-base leading-7 text-slate-600 sm:text-lg">
            Bitte prüfe die vorbereiteten Angaben in Ruhe. Wache, Wachabteilung, Zeitraum und Preis werden verbindlich vom System vorgegeben.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="grid gap-6 lg:grid-cols-[minmax(0,1.25fr)_minmax(320px,.75fr)] lg:items-start">
          <div className="space-y-6">
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-red-50 text-red-700"><FileCheck2 className="h-5 w-5" /></span>
                <div><h2 className="text-xl font-semibold">Vertragspartner prüfen</h2><p className="text-sm text-slate-500">Du schließt den Vertrag als Privatperson.</p></div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="text-sm font-medium text-slate-700">Vollständiger Name
                  <input value={party.name} onChange={(e) => setParty({ ...party, name: e.target.value })} required minLength={2} autoComplete="name" className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-950" />
                </label>
                <label className="text-sm font-medium text-slate-700">E-Mail-Adresse
                  <input type="email" value={party.email} onChange={(e) => setParty({ ...party, email: e.target.value })} required autoComplete="email" className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-950" />
                </label>
              </div>
            </section>

            <ContractSummary contract={contract} party={party} />

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
              <h2 className="mb-4 text-xl font-semibold">Vertrag und Widerruf</h2>
              <div className="space-y-3">
                <LegalDetails title={`${legal.usage_contract?.title || "Nutzungsvertrag"} · Version ${legal.usage_contract?.version || "–"}`} html={legal.usage_contract?.html} open />
                <LegalDetails title={`${legal.withdrawal_policy?.title || "Widerrufsbelehrung"} · Version ${legal.withdrawal_policy?.version || "–"}`} html={legal.withdrawal_policy?.html} />
              </div>
            </section>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-6">
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <h2 className="text-lg font-semibold">Verbindlich bestätigen</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">Keine Auswahl ist vorausgewählt. Bitte bestätige beide Punkte bewusst.</p>
              <div className="mt-5 space-y-4">
                <CheckRow checked={acceptedTerms} onChange={setAcceptedTerms}>Ich habe den Nutzungsvertrag gelesen und akzeptiere die Vertragsbedingungen.</CheckRow>
                <CheckRow checked={acknowledgedWithdrawal} onChange={setAcknowledgedWithdrawal}>Ich habe die Datenschutzerklärung und die Widerrufsbelehrung zur Kenntnis genommen.</CheckRow>
              </div>
              {error && <div role="alert" className="mt-5 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
              <button type="submit" disabled={!submitReady || submitting} className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-red-700 px-4 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-800 disabled:cursor-not-allowed disabled:bg-slate-300">
                {submitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Zahlungspflichtig abschließen – {formatEuro(contract.terms.price_cents)}
              </button>
              <p className="mt-3 text-center text-xs leading-5 text-slate-500">Vorauszahlung per PayPal oder Überweisung. Kein Lastschrift- oder Kreditkartenabo.</p>
            </section>
            <div className="rounded-xl border border-slate-200 px-4 py-3 text-xs leading-5 text-slate-500">Betreiber: Jonas Parlow · c/o Block Services · Stuttgarter Str. 106 · 70736 Fellbach</div>
          </aside>
        </form>
      </main>
    </PublicShell>
  );
}

const CheckRow = ({ checked, onChange, children }) => (
  <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-3.5 text-sm leading-5 text-slate-700 transition hover:border-slate-300">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 accent-red-700" />
    <span>{children}</span>
  </label>
);

const LegalDetails = ({ title, html, open = false }) => (
  <details open={open} className="group rounded-xl border border-slate-200 bg-slate-50/50">
    <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold text-slate-800">{title}</summary>
    <div className="contract-legal border-t border-slate-200 bg-white px-4 py-5" dangerouslySetInnerHTML={{ __html: html || "" }} />
  </details>
);

export const PublicShell = ({ children }) => (
  <div className="min-h-screen bg-[#f4f2ee] px-4 py-6 sm:px-8 sm:py-10">
    <div className="mx-auto mb-8 flex max-w-5xl items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-700 p-1.5 shadow-sm"><img src="/logo.png" alt="" className="h-full w-full object-contain" /></div>
      <div><div className="text-sm font-semibold text-slate-950">Feuerwehr Kantine</div><div className="text-xs text-slate-500">Vertragsportal</div></div>
    </div>
    {children}
  </div>
);
